import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ParentalPinType } from '@kaltura-ott/tvpil-shared';

import { DescriptionWithAge } from 'components/widgets/Modal/PinCodeModal/DescriptionWithAge/DescriptionWithAge';
import PinCodeModal from 'components/widgets/Modal/PinCodeModal/PinCodeModal';
import { PinCodeModalTypes } from 'components/widgets/Modal/PinCodeModal/PinCodeModalTypesEnum';
import UnPurchasedContentModal from 'components/widgets/Modal/UnPurchasedContentModal/UnPurchasedContentModal';
import { handleRouteUpdates } from 'components/widgets/Player/handlers';
import { playerStorage, startOverInfoStorage } from 'components/widgets/Player/storages';
import { LAST_URL_PLAY_BACK } from 'consts';
import { useErrorScreenContext } from 'context/error/error';
import { useModalContext } from 'context/modal/modal';
import { usePerformanceEventsContext } from 'context/performanceEvents/performanceEvents';
import { useGoBack } from 'hooks';
import { PerformanceEvents } from 'interface';
import { globalStorage } from 'services/globalStorage';
import { getPerformanceAdditionalParams } from 'utils';

import { useBlockContentResolver } from '../useBlockContentResolver/useBlockContentResolver';
import { useSetGeoError } from '../useSetGeoError/useSetGeoError';

export const useCheckRestrictions = () => {
  const [forceUpdate, setForceUpdate] = useState(false);
  const { openModal, closeModal } = useModalContext();
  const { setError, clearError } = useErrorScreenContext();
  const performanceEventsContext = usePerformanceEventsContext();
  const location = useLocation();
  const goBack = useGoBack();
  const setGeoError = useSetGeoError();
  const {
    isContentBlockedAsAdult,
    geoBlockingProperties,
    isContentRestricted,
    isPlaybackAllowed,
    parentalPinStore,
    parentalRating,
    isNotPurchased,
    isGeoBlocking,
    isPlayerError,
    asset,
  } = useBlockContentResolver();

  useEffect(() => {
    if (isContentRestricted && asset) {
      const { assetId } = getPerformanceAdditionalParams();
      const modalType = isContentBlockedAsAdult
        ? PinCodeModalTypes.RestrictedContent
        : PinCodeModalTypes.RestrictedContentParental;
      const pinType = isContentBlockedAsAdult ? ParentalPinType.adult : ParentalPinType.parental;
      const description =
        !isContentBlockedAsAdult && parentalRating ? <DescriptionWithAge age={parentalRating} /> : undefined;
      const onDecline = () => {
        goBack();
      };

      performanceEventsContext.setEndTime({
        value: PerformanceEvents.purchase,
        param1: assetId,
      });

      handleRouteUpdates(); // to update url during autoplay

      const validateAsset = (pin: string) => parentalPinStore.validatePin(pin, pinType, asset);

      openModal(
        <PinCodeModal
          callback={(success: boolean) => {
            if (success) {
              // to update performance event start time if pin code modal appears
              performanceEventsContext.setStartTime(PerformanceEvents.playbackStart);
              setForceUpdate(!forceUpdate);
            }
          }}
          onDecline={onDecline}
          description={description}
          type={modalType}
          validatePin={validateAsset}
        />,
      );
    }

    if (isNotPurchased) {
      openModal(<UnPurchasedContentModal />);
    }

    if (!isPlaybackAllowed) {
      globalStorage.setPossiblePresentPlayer(false);
    }

    if (isGeoBlocking) {
      startOverInfoStorage.resetStartOverInfo();

      setGeoError();
      return;
    }

    return () => {
      // TO avoid calling the clearError and closeModal if url is not changed
      if (location.pathname !== sessionStorage.getItem(LAST_URL_PLAY_BACK)) {
        clearError();
        closeModal();
        playerStorage.clearIsPlayBackPausedAndSavedCurrentTime();
        globalStorage.clearIsPlayerError();
        sessionStorage.removeItem(LAST_URL_PLAY_BACK);
      }
    };
  }, [
    isContentBlockedAsAdult,
    isContentRestricted,
    isGeoBlocking,
    isNotPurchased,
    isPlaybackAllowed,
    performanceEventsContext,
    geoBlockingProperties,
    asset,
    parentalRating,
    openModal,
    closeModal,
    setError,
    clearError,
    parentalPinStore,
    isPlayerError,
  ]);

  return isPlaybackAllowed;
};
